"use client";

/**
 * WhatsAppCheckoutDialog
 *
 * Customer-facing checkout dialog opened from the CartDrawer. Mirrors
 * the visual language of `components/shared/WhatsAppChatDialog.tsx` so
 * customers feel like they're already inside WhatsApp:
 *   - Dark green WhatsApp header (#075E54)
 *   - WhatsApp wallpaper texture on the body
 *   - Light-green "outgoing message" bubble for the order summary
 *   - White "incoming message" bubble for the form
 *   - Pill-shaped form controls + send button
 *
 * Layout:
 *   - Desktop (≥ md): docked at bottom-right, 400×~640px, like the chat
 *     widget. Backdrop is pointer-events:none on desktop so users can
 *     interact with the page behind it (mirrors the chat widget).
 *   - Mobile (< md): full-screen sheet (inset-4) with a translucent
 *     backdrop. Backdrop click does NOT dismiss — only the X button.
 *
 * Submission states (rendered in-place so the dialog never disappears):
 *   - "form"    → inputs visible
 *   - "submitting" → "Creating your order…" loader replaces the inputs
 *   - "success"  → green check + order number, auto-closes after 2.5s
 */

import { useState } from "react";
import { useMutation } from "convex/react";
import { api } from "@/convex/_generated/api";
import { Id } from "@/convex/_generated/dataModel";
import { CartItem, AppliedPromo } from "@/lib/store/cartStore";
import { X, Loader2, CheckCircle2, Send, ShoppingBag } from "lucide-react";
import { toast } from "@/lib/toast";
import Image from "next/image";
import type { CountryCode } from "libphonenumber-js";
import {
  CountryPhoneInput,
  toE164,
  isPhoneValid,
} from "@/components/ui/CountryPhoneInput";
import { choicesFor, type BagLineSummary } from "@/lib/config/websiteAddOns";
import { friendlyError } from "@/lib/utils/error-utils";

interface WhatsAppCheckoutDialogProps {
  isOpen: boolean;
  onClose: () => void;
  cartItems: CartItem[];
  /** Each item's add-ons, as the bag shows them (lib/config/websiteAddOns.ts bagSummary). */
  lines?: BagLineSummary[];
  subtotal: number;
  appliedPromo: AppliedPromo | null;
  totalPrice: number;
  brandId: Id<"brands"> | undefined;
  brandName?: string;
  /** Called after successful order submission — parent should clear cart + close. */
  onSuccess: () => void | Promise<void>;
}

// Inline WhatsApp glyph — same SVG used by WhatsAppChatDialog for parity.
function WhatsAppIcon({ size = 20 }: { size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      width={size}
      height={size}
      aria-hidden
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}

// WhatsApp-style chat wallpaper texture (same SVG as WhatsAppChatDialog).
const CHAT_BG_PATTERN = `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4cfc4' fill-opacity='0.3'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`;

type Stage = "form" | "submitting" | "success";

export function WhatsAppCheckoutDialog({
  isOpen,
  onClose,
  cartItems,
  lines,
  subtotal,
  appliedPromo,
  totalPrice,
  brandId,
  brandName,
  onSuccess,
}: WhatsAppCheckoutDialogProps) {
  const [name, setName] = useState("");
  // `phone` holds the national number the customer types; `country` holds
  // the selected ISO. The full E.164 number is derived at submit time via
  // toE164() so buyers from any country (Kenya, Nigeria, …) work — not just
  // Uganda. Default to UG since that's the primary market.
  const [country, setCountry] = useState<CountryCode>("UG");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [stage, setStage] = useState<Stage>("form");
  const [orderNumber, setOrderNumber] = useState<string | null>(null);

  const phoneValid = isPhoneValid(country, phone);

  // @ts-ignore — Convex deep instantiation flake on mutation refs
  const submitMutation = useMutation((api.orders.whatsappCheckout as any).submitWhatsAppCheckout);

  const reset = () => {
    setName("");
    setCountry("UG");
    setPhone("");
    setNotes("");
    setStage("form");
    setOrderNumber(null);
  };

  const handleClose = () => {
    if (stage === "submitting") return; // never close mid-submit
    reset();
    onClose();
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!brandId) {
      toast.error("Cart is empty");
      return;
    }
    if (!name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (!phone.trim()) {
      toast.error("Please enter your WhatsApp number");
      return;
    }
    // Combine the chosen country code + typed number into a full E.164
    // string (e.g. "+254712345678"). If it doesn't parse to a valid number
    // for that country, stop and tell the user instead of sending a
    // Uganda-coerced number to the backend.
    const e164 = toE164(country, phone);
    if (!e164 || !isPhoneValid(country, phone)) {
      toast.error("Please enter a valid phone number for the selected country");
      return;
    }
    if (cartItems.length === 0) {
      toast.error("Your cart is empty");
      return;
    }

    setStage("submitting");
    try {
      const result = await submitMutation({
        brandId,
        customerName: name.trim(),
        customerPhone: e164,
        items: cartItems.map((item, i) => {
          // The extras ticked: ids and counts — the server prices them.
          const addOns = choicesFor(lines?.[i]?.paid);
          return {
            productId: item.productId,
            variantId: item.variantId,
            size: item.selectedVariants?.size,
            color: item.selectedVariants?.color,
            quantity: item.quantity,
            ...(addOns.length ? { addOns } : {}),
          };
        }),
        promoCode: appliedPromo?.code,
        notes: notes.trim() || undefined,
      });

      setOrderNumber(result.orderNumber);
      setStage("success");

      // Brief celebration, then clear + close.
      setTimeout(() => {
        onSuccess();
        reset();
      }, 2500);
    } catch (err: any) {
      console.error("[WhatsAppCheckout] submission failed:", err);
      toast.error(friendlyError(err, "Failed to send order. Please try again."));
      setStage("form");
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop — visible on mobile only, no click-to-close on either size */}
      <div
        className="fixed inset-0 z-[80] bg-black/50 md:bg-transparent md:pointer-events-none"
        aria-hidden
      />

      {/* Dialog shell — bottom-right docked on desktop, full-screen sheet on mobile.
          Uses Tailwind's slide-in-from-bottom + fade for a snappy entrance. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Order on WhatsApp"
        className="fixed z-[81] inset-4 md:inset-auto md:bottom-6 md:right-6 md:w-[400px] md:max-h-[calc(100vh-48px)] flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 fade-in duration-200"
        style={{ maxHeight: "calc(100vh - 32px)" }}
      >
        {/* WhatsApp-style header */}
        <div
          className="flex items-center justify-between px-4 py-3 shrink-0"
          style={{ backgroundColor: "#075E54", color: "#ffffff" }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
              style={{ background: "rgba(255,255,255,0.15)" }}
            >
              <WhatsAppIcon size={20} />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-sm leading-tight truncate">
                Order on WhatsApp
              </h3>
              <p
                className="text-[11px] leading-tight truncate"
                style={{ color: "#bbf7d0" }}
              >
                {brandName || "Place your order in seconds"}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={stage === "submitting"}
            aria-label="Close"
            className="p-2 rounded-full transition-colors disabled:opacity-50"
            style={{
              color: "#ffffff",
              background: "transparent",
            }}
            onMouseEnter={(e) => {
              if (stage !== "submitting")
                e.currentTarget.style.background = "rgba(255,255,255,0.12)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
            }}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body — chat wallpaper background */}
        <div
          className="flex-1 overflow-y-auto"
          style={{
            backgroundImage: CHAT_BG_PATTERN,
            backgroundColor: "#E4DDD6",
          }}
        >
          {stage === "submitting" ? (
            // ── Inline loading state ─────────────────────────────────────
            <div className="flex flex-col items-center justify-center min-h-[400px] p-6 text-center">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mb-4 shadow-sm"
                style={{ backgroundColor: "#ffffff" }}
              >
                <Loader2
                  className="w-7 h-7 animate-spin"
                  style={{ color: "#075E54" }}
                />
              </div>
              <p
                className="text-sm font-semibold"
                style={{ color: "#111827" }}
              >
                Creating your order…
              </p>
              <p className="text-xs mt-1.5 max-w-xs" style={{ color: "#4b5563" }}>
                We're preparing your WhatsApp order with{" "}
                <span className="font-medium">
                  {brandName || "the seller"}
                </span>
                . This will just take a moment.
              </p>
            </div>
          ) : stage === "success" ? (
            // ── Success state ────────────────────────────────────────────
            <div className="flex flex-col items-center justify-center min-h-[400px] p-6 text-center">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mb-4 shadow-md"
                style={{ backgroundColor: "#25D366" }}
              >
                <CheckCircle2 className="w-9 h-9 text-white" />
              </div>
              <h3
                className="text-lg font-bold mb-1"
                style={{ color: "#111827" }}
              >
                Order sent! 🎉
              </h3>
              <p className="text-xs mb-3" style={{ color: "#6b7280" }}>
                Order #{orderNumber}
              </p>
              <p
                className="text-sm max-w-xs"
                style={{ color: "#374151" }}
              >
                Check your WhatsApp — {brandName || "the seller"} will
                reach out shortly to confirm.
              </p>
            </div>
          ) : (
            // ── Form state ───────────────────────────────────────────────
            <div className="p-3 space-y-3">
              {/* Greeting bubble (incoming, white, top-left tail) */}
              <div className="flex">
                <div
                  className="max-w-[85%] px-3 py-2 rounded-lg shadow-sm"
                  style={{
                    backgroundColor: "#ffffff",
                    borderTopLeftRadius: 0,
                  }}
                >
                  <p
                    className="text-sm leading-snug"
                    style={{ color: "#111827" }}
                  >
                    👋 Hi! Just enter your name and WhatsApp number — we'll
                    send your order to{" "}
                    <span className="font-semibold">
                      {brandName || "the seller"}
                    </span>{" "}
                    and confirm via WhatsApp.
                  </p>
                </div>
              </div>

              {/* Cart preview bubble (outgoing, light green, top-right tail) */}
              <div className="flex justify-end">
                <div
                  className="max-w-[85%] px-3 py-2.5 rounded-lg shadow-sm"
                  style={{
                    backgroundColor: "#DCF8C6",
                    borderTopRightRadius: 0,
                  }}
                >
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <ShoppingBag
                      className="w-3.5 h-3.5"
                      style={{ color: "#075E54" }}
                    />
                    <span
                      className="text-[11px] font-semibold uppercase tracking-wide"
                      style={{ color: "#075E54" }}
                    >
                      Your order ({cartItems.length} item
                      {cartItems.length !== 1 ? "s" : ""})
                    </span>
                  </div>

                  {/* Items list */}
                  <div className="space-y-1.5 mb-2 max-h-32 overflow-y-auto">
                    {cartItems.map((item, i) => (
                      <div key={item._id} className="flex items-center gap-2">
                        <div className="relative w-8 h-8 rounded overflow-hidden bg-white shrink-0 border border-black/5">
                          {item.productImage && (
                            <Image
                              src={item.productImage}
                              alt={item.productName}
                              fill
                              className="object-cover"
                            />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p
                            className="text-[12px] font-medium leading-tight truncate"
                            style={{ color: "#111827" }}
                          >
                            {item.productName}
                          </p>
                          <p
                            className="text-[10px] leading-tight"
                            style={{ color: "#4b5563" }}
                          >
                            {item.quantity} ×{" "}
                            {item.price.toLocaleString()}{" "}
                            UGX
                          </p>
                          {/* What it comes with, and the extras ticked (Oct 2026). */}
                          {(lines?.[i]?.free.length ?? 0) > 0 && (
                            <p className="text-[10px] leading-tight" style={{ color: "#4b5563" }}>
                              with {lines![i].free.map((p) => `${p.qty * item.quantity} × ${p.name}`).join(", ")}
                            </p>
                          )}
                          {(lines?.[i]?.notIncluded.length ?? 0) > 0 && (
                            <p className="text-[10px] leading-tight" style={{ color: "#6b7280" }}>
                              not included (out of stock): {lines![i].notIncluded.map((p) => `${p.qty * item.quantity} × ${p.name}`).join(", ")}
                            </p>
                          )}
                          {lines?.[i]?.paid.map((p) => (
                            <p key={p.id} className="text-[10px] leading-tight" style={{ color: "#4b5563" }}>
                              + {p.qty * item.quantity} × {p.name} · {(p.price * p.qty * item.quantity).toLocaleString()} UGX
                            </p>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Totals */}
                  <div
                    className="space-y-0.5 pt-1.5 border-t"
                    style={{ borderColor: "rgba(7,94,84,0.15)" }}
                  >
                    <div className="flex items-center justify-between text-[11px]">
                      <span style={{ color: "#4b5563" }}>Subtotal</span>
                      <span
                        className="font-medium"
                        style={{ color: "#111827" }}
                      >
                        {Math.floor(subtotal).toLocaleString()} UGX
                      </span>
                    </div>
                    {appliedPromo && appliedPromo.discount > 0 && (
                      <div className="flex items-center justify-between text-[11px]">
                        <span style={{ color: "#075E54" }}>
                          {appliedPromo.code}{" "}
                          {appliedPromo.type === "percentage"
                            ? `(${appliedPromo.discountValue}%)`
                            : ""}
                        </span>
                        <span
                          className="font-medium"
                          style={{ color: "#075E54" }}
                        >
                          -
                          {Math.floor(appliedPromo.discount).toLocaleString()}{" "}
                          UGX
                        </span>
                      </div>
                    )}
                    <div className="flex items-center justify-between text-[12px] pt-0.5">
                      <span
                        className="font-semibold"
                        style={{ color: "#111827" }}
                      >
                        Total
                      </span>
                      <span
                        className="font-bold"
                        style={{ color: "#111827" }}
                      >
                        {Math.floor(totalPrice).toLocaleString()} UGX
                      </span>
                    </div>
                  </div>
                  <p
                    className="text-[10px] mt-1.5"
                    style={{ color: "#4b5563" }}
                  >
                    💵 Pay on delivery
                  </p>
                </div>
              </div>

              {/* Form bubble (incoming, white) — fields stacked inside */}
              <div className="flex">
                <div
                  className="w-full max-w-full px-3 py-3 rounded-lg shadow-sm space-y-2.5"
                  style={{
                    backgroundColor: "#ffffff",
                    borderTopLeftRadius: 0,
                  }}
                >
                  <p
                    className="text-[11px] font-semibold uppercase tracking-wide"
                    style={{ color: "#075E54" }}
                  >
                    Your details
                  </p>

                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Full name"
                    required
                    className="w-full px-3 py-2 text-sm rounded-lg outline-none transition-colors"
                    style={{
                      backgroundColor: "#f3f4f6",
                      border: "1px solid transparent",
                      color: "#111827",
                    }}
                    onFocus={(e) =>
                      (e.currentTarget.style.borderColor = "#075E54")
                    }
                    onBlur={(e) =>
                      (e.currentTarget.style.borderColor = "transparent")
                    }
                  />

                  {/* Country-aware phone input — customers from any country
                      can pick their code or search for it. No longer locked
                      to Uganda. */}
                  <CountryPhoneInput
                    country={country}
                    onCountryChange={setCountry}
                    value={phone}
                    onChange={setPhone}
                    placeholder="700 123 456"
                    accentColor="#075E54"
                  />
                  {phone.trim() && !phoneValid && (
                    <p className="text-[11px] text-red-500 -mt-1">
                      Enter a valid number for the selected country
                    </p>
                  )}

                  <input
                    type="text"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Note for the seller (optional)"
                    maxLength={200}
                    className="w-full px-3 py-2 text-sm rounded-lg outline-none transition-colors"
                    style={{
                      backgroundColor: "#f3f4f6",
                      border: "1px solid transparent",
                      color: "#111827",
                    }}
                    onFocus={(e) =>
                      (e.currentTarget.style.borderColor = "#075E54")
                    }
                    onBlur={(e) =>
                      (e.currentTarget.style.borderColor = "transparent")
                    }
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer — input bar style, with Send button */}
        {stage === "form" && (
          <form
            onSubmit={handleSubmit}
            className="shrink-0 flex items-center gap-2 px-3 py-3"
            style={{ backgroundColor: "#F0F0F0" }}
          >
            <p
              className="flex-1 text-[11px] leading-snug"
              style={{ color: "#4b5563" }}
            >
              Tap send — we'll confirm via WhatsApp.
            </p>
            <button
              type="submit"
              disabled={!name.trim() || !phoneValid || cartItems.length === 0}
              className="h-10 px-4 rounded-full flex items-center gap-2 text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              style={{
                backgroundColor: "#075E54",
                color: "#ffffff",
              }}
              onMouseEnter={(e) => {
                if (!e.currentTarget.disabled)
                  e.currentTarget.style.backgroundColor = "#064E46";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#075E54";
              }}
            >
              <Send className="w-4 h-4" />
              Send order
            </button>
          </form>
        )}
      </div>
    </>
  );
}
